import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { LoadingSpinner } from '../../../components/LoadingSpinner';
import {
  activateAlertTemplateVersion,
  AdminQueryKeys,
  ALERT_TYPES,
  createAlertTemplateVersion,
  getAlertTemplate,
  previewAlertTemplate,
} from '../api';
import { EmailAlertAdminTabs } from '../components/EmailAlertAdminTabs';
import {
  EmailDesignCanvas,
  type EmailDesignCanvasHandle,
} from '../components/email/EmailDesignCanvas';
import { DEFAULT_ACCENT, detectAccentColor } from '../components/email/emailTemplateEditing';
import type { HtmlCodeEditorHandle } from '../components/email/HtmlCodeEditor';

// CodeMirror is heavy and only needed on the "Código" tab — load it on demand so it never lands in
// the admin's initial bundle.
const HtmlCodeEditor = lazy(() =>
  import('../components/email/HtmlCodeEditor').then((m) => ({ default: m.HtmlCodeEditor })),
);

type Device = 'desktop' | 'mobile';
type EditorTab = 'design' | 'code';

const ALERT_TYPE_LABEL: Record<string, string> = {
  NearUsageLimit: 'Cerca del límite de uso',
  WeeklyUsageSummary: 'Resumen semanal',
};

/**
 * Email-template studio. A hybrid editor over the versioned template for each alert type:
 *   - "Diseño": the rendered email is editable in place (click text to edit); tokens show as
 *     protected chips and an accent-colour control recolours the brand blue.
 *   - "Código": the raw HTML with CodeMirror syntax highlighting, for structural edits.
 * Both write the same HTML body (the source of truth — tokens, server renderer and versioning
 * stay intact). The right pane is the authoritative server-rendered preview (real sample values),
 * which also validates unknown tokens. Saving creates a new active version; older ones can be
 * re-activated (rollback).
 */
export function AlertTemplatesPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [alertType, setAlertType] = useState<string>(ALERT_TYPES[0] ?? 'NearUsageLimit');
  const { data, isLoading, isError } = useQuery({
    queryKey: AdminQueryKeys.alertTemplate(alertType),
    queryFn: ({ signal }) => getAlertTemplate(alertType, signal),
  });

  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [notes, setNotes] = useState('');
  const [device, setDevice] = useState<Device>('desktop');
  const [tab, setTab] = useState<EditorTab>('design');
  const [saveError, setSaveError] = useState<string | null>(null);

  // Accent colour: `applied` is the colour currently present in the HTML, `picker` is the input value.
  const [appliedAccent, setAppliedAccent] = useState<string>(DEFAULT_ACCENT);
  const [accentColor, setAccentColor] = useState<string>(DEFAULT_ACCENT);

  // Bumped whenever an EXTERNAL change replaces the body (new template loaded / saved), forcing the
  // uncontrolled design canvas to remount and reseed. The canvas's own edits never bump this.
  const [seedKey, setSeedKey] = useState(0);

  // Live preview state, refreshed on a debounce by the effect below.
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');
  const [previewError, setPreviewError] = useState<string | null>(null);
  const previewSeq = useRef(0);

  const canvasRef = useRef<EmailDesignCanvasHandle>(null);
  const codeRef = useRef<HtmlCodeEditorHandle>(null);

  // Reset the editor to the active version whenever the loaded template changes.
  useEffect(() => {
    if (data !== undefined) {
      setSubject(data.activeSubject);
      setBody(data.activeHtmlBody);
      setNotes('');
      setSaveError(null);
      const accent = detectAccentColor(data.activeHtmlBody, [DEFAULT_ACCENT]);
      setAppliedAccent(accent);
      setAccentColor(accent);
      setSeedKey((k) => k + 1);
    }
  }, [data]);

  // Debounced live preview: render the current draft against the sample model server-side. The seq
  // guard drops out-of-order responses; unknown tokens surface as previewError.
  useEffect(() => {
    if (subject.trim() === '' && body.trim() === '') {
      return;
    }
    const seq = ++previewSeq.current;
    const handle = window.setTimeout(() => {
      previewAlertTemplate(alertType, { subject, htmlBody: body })
        .then((result) => {
          if (seq !== previewSeq.current) {
            return;
          }
          setPreviewHtml(result.renderedHtmlBody);
          setPreviewSubject(result.renderedSubject);
          setPreviewError(null);
        })
        .catch((err: unknown) => {
          if (seq !== previewSeq.current) {
            return;
          }
          setPreviewError(err instanceof Error ? err.message : 'No se pudo previsualizar la plantilla.');
        });
    }, 450);
    return () => window.clearTimeout(handle);
  }, [subject, body, alertType]);

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: AdminQueryKeys.alertTemplate(alertType) });
  };
  const create = useMutation({
    mutationFn: () => createAlertTemplateVersion(alertType, { subject, htmlBody: body, notes: notes === '' ? null : notes }),
    onSuccess: () => { setSaveError(null); invalidate(); },
    onError: (e: unknown) => setSaveError(e instanceof Error ? e.message : 'No se pudo guardar.'),
  });
  const activate = useMutation({
    mutationFn: (versionId: string) => activateAlertTemplateVersion(alertType, versionId),
    onSuccess: invalidate,
  });

  // Route a token insertion to whichever editor is active.
  const insertToken = (name: string): void => {
    if (tab === 'design') {
      canvasRef.current?.insertToken(name);
    } else {
      codeRef.current?.insertAtCursor(`{{${name}}}`);
    }
  };

  const onPickAccent = (hex: string): void => {
    setAccentColor(hex);
    canvasRef.current?.applyAccentColor(appliedAccent, hex);
    setAppliedAccent(hex);
  };

  const canvasWidth: number | string = device === 'mobile' ? 360 : '100%';

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-semibold text-slate-900">Plantillas de alerta</h1>
        <p className="mt-1 text-sm text-slate-500">
          Edita el correo de cada alerta sobre su diseño final o por código, y previsualízalo en vivo. Guardar crea una versión nueva activa; puedes revertir a una anterior.
        </p>
      </header>

      <EmailAlertAdminTabs />

      <label className="block text-sm text-slate-700">
        Tipo de alerta
        <select
          className="input mt-1 max-w-xs"
          value={alertType}
          onChange={(e) => setAlertType(e.target.value)}
          data-testid="template-type"
        >
          {ALERT_TYPES.map((t) => (
            <option key={t} value={t}>{ALERT_TYPE_LABEL[t] ?? t}</option>
          ))}
        </select>
      </label>

      {isLoading ? (
        <div className="flex justify-center py-8"><LoadingSpinner /></div>
      ) : isError || data === undefined ? (
        <div className="card border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">No se pudo cargar la plantilla.</div>
      ) : (
        <>
          <div className="grid gap-4 lg:grid-cols-2">
            {/* ---- Editor ---- */}
            <div className="card space-y-3 p-4">
              {/* Tab switch */}
              <div className="flex gap-1" role="group" aria-label="Modo de edición">
                {(['design', 'code'] as const).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setTab(t)}
                    aria-pressed={tab === t}
                    className={
                      'rounded-md px-3 py-1.5 text-sm font-medium ' +
                      (tab === t ? 'bg-primary text-white' : 'border border-slate-300 text-slate-600 hover:bg-slate-50')
                    }
                    data-testid={`tab-${t}`}
                  >
                    {t === 'design' ? 'Diseño' : 'Código'}
                  </button>
                ))}
              </div>

              <div>
                <p className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">Tokens (clic para insertar)</p>
                <div className="flex flex-wrap gap-1.5">
                  {data.tokens.map((t) => (
                    <button
                      key={t.name}
                      type="button"
                      onClick={() => insertToken(t.name)}
                      className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 font-mono text-xs text-slate-600 transition-colors hover:border-primary hover:text-primary"
                      data-testid={`token-${t.name}`}
                    >
                      {`{{${t.name}}}`}
                    </button>
                  ))}
                </div>
              </div>

              <label className="block text-sm text-slate-700">
                Asunto
                <input className="input mt-1" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="template-subject" />
              </label>

              {tab === 'design' ? (
                <div className="flex items-center gap-2 text-sm text-slate-700">
                  <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Color de acento</span>
                  <input
                    type="color"
                    value={accentColor}
                    onChange={(e) => onPickAccent(e.target.value)}
                    className="h-7 w-10 cursor-pointer rounded border border-slate-300 bg-white p-0.5"
                    aria-label="Color de acento"
                    data-testid="accent-color"
                  />
                  <span className="font-mono text-xs text-slate-400">{accentColor}</span>
                </div>
              ) : null}

              {/* Editor surface */}
              {tab === 'design' ? (
                <EmailDesignCanvas
                  key={seedKey}
                  ref={canvasRef}
                  sourceHtml={body}
                  width={canvasWidth}
                  onChange={setBody}
                />
              ) : (
                <Suspense fallback={<div className="flex justify-center py-8"><LoadingSpinner /></div>}>
                  <HtmlCodeEditor ref={codeRef} value={body} onChange={setBody} />
                </Suspense>
              )}

              <input className="input" placeholder="Notas de la versión (opcional)" value={notes} onChange={(e) => setNotes(e.target.value)} />
              {saveError !== null ? <p className="text-xs text-rose-700">{saveError}</p> : null}
              <button
                type="button"
                onClick={() => create.mutate()}
                disabled={create.isPending || subject.trim() === '' || body.trim() === '' || previewError !== null}
                className="btn-primary"
                data-testid="template-save"
              >
                {create.isPending ? 'Guardando…' : 'Guardar como nueva versión'}
              </button>
              {previewError !== null ? (
                <p className="text-xs text-rose-700">Corrige el error de previsualización antes de guardar.</p>
              ) : null}
            </div>

            {/* ---- Live (server) preview ---- */}
            <div className="space-y-2 lg:sticky lg:top-4 lg:self-start">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Resultado real (servidor)</span>
                <div className="flex gap-1" role="group" aria-label="Ancho de previsualización">
                  {(['desktop', 'mobile'] as const).map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setDevice(d)}
                      aria-pressed={device === d}
                      className={
                        'rounded-md px-2.5 py-1 text-xs font-medium ' +
                        (device === d ? 'bg-primary text-white' : 'border border-slate-300 text-slate-600 hover:bg-slate-50')
                      }
                      data-testid={`preview-${d}`}
                    >
                      {d === 'desktop' ? 'Escritorio' : 'Móvil'}
                    </button>
                  ))}
                </div>
              </div>

              {previewError !== null ? (
                <div className="card border-rose-200 bg-rose-50 p-4 text-sm text-rose-800" data-testid="preview-error">
                  {previewError}
                </div>
              ) : (
                <div className="overflow-hidden rounded-lg border border-slate-200 bg-slate-100 p-3">
                  {/* Inbox-style header */}
                  <div className="mb-3 rounded-md bg-white px-3 py-2 shadow-sm">
                    <p className="truncate text-sm font-semibold text-slate-900" data-testid="preview-subject">
                      {previewSubject || '—'}
                    </p>
                    <p className="text-xs text-slate-500">DocuSense · alertas</p>
                  </div>
                  <div className="mx-auto transition-all" style={{ width: device === 'mobile' ? 360 : '100%', maxWidth: 600 }}>
                    <iframe
                      title="Previsualización del correo"
                      sandbox=""
                      srcDoc={previewHtml}
                      className="h-[560px] w-full rounded-md border border-slate-200 bg-white"
                      data-testid="preview-frame"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ---- Version history ---- */}
          <div className="card overflow-hidden">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-slate-200 bg-slate-50 text-xs font-medium uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3">#</th>
                  <th className="px-4 py-3">Autor</th>
                  <th className="px-4 py-3">Notas</th>
                  <th className="px-4 py-3 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.versions.map((v) => (
                  <tr key={v.versionId} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-slate-700">{v.number}{v.isActive ? ' ★' : ''}</td>
                    <td className="px-4 py-3 text-slate-700">{v.createdBy}</td>
                    <td className="px-4 py-3 text-slate-500">{v.notes ?? '—'}</td>
                    <td className="px-4 py-3 text-right">
                      {v.isActive ? (
                        <span className="text-xs text-slate-400">Activa</span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => activate.mutate(v.versionId)}
                          disabled={activate.isPending}
                          className="text-xs font-medium text-primary hover:underline disabled:opacity-50"
                        >
                          Activar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
